import { getMapManager } from '../components/Map/MapManager.js';
import { transitionToPosition } from './mapCameraUtils.js';

export class NiraManager {
    constructor() {
        this.container = document.getElementById('nira');
        this.iframe = document.getElementById('niraViewer'); // Capital V to match HTML
        this.closeBtn = this.container.querySelector('.nira-close');
        this.toolbar = document.getElementById('toolbar');
        this.niraTools = document.getElementById('niraTools');
        
        // UI elements to manage visibility
        this.cityNav = document.getElementById('cityNav');
        this.infoPanel = document.querySelector('.info-container');
        this.dataPanel = document.querySelector('.data-panel');
        this.tools = document.getElementById('tools');
        
        // Get map container which has our Statamic settings as data attributes
        const mapContainer = document.getElementById('map');
        
        // Base URL for Nira viewer - get from Statamic global or fall back to default
        this.niraBaseUrl = mapContainer?.dataset.niraLink || 'https://artefex.nira.app/a/I12JDI7VQWmdddpIWrA/1';
        
        // Add autoplay parameter to URL if possible
        this.niraUrl = this.getNiraUrlWithParams();
        
        // Hardcoded camera position for Nira view
        this.cameraPosition = {
            center: [39.610282, 24.463219],
            zoom: 14.71,
            pitch: 62.27,
            bearing: 0.00
        };
        
        // Nira API viewer instance
        this.niraViewer = null;
        
        this.setupEventListeners();
        this.setupVisibilityObserver();
    }
    
    /**
     * Set up a MutationObserver to watch for changes to the Nira container's visibility
     * This provides an additional way to ensure UI elements are hidden when Nira is visible
     */
    setupVisibilityObserver() {
        if (!this.container) return;
        
        // Create a MutationObserver to watch for class changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    // Check if the Nira container is now visible
                    if (this.container.classList.contains('visible')) {
                        console.log('[NiraManager] Nira container is now visible, hiding UI elements');
                        this.hideUIElements();
                    }
                }
            });
        });
        
        // Start observing the container for class changes
        observer.observe(this.container, { attributes: true });
        console.log('[NiraManager] Set up visibility observer for Nira container');
    }
    
    /**
     * Constructs the Nira URL with autoplay parameters
     * Many 3D viewers support URL parameters to control initial state
     */
    getNiraUrlWithParams() {
        // Try common parameter names for autoplay/auto-start
        const params = new URLSearchParams({
            autoplay: 'true',
            auto_start: 'true',
            start: 'true',
            play: 'true'
        });
        
        return `${this.niraBaseUrl}?${params.toString()}`;
    }
    
    setupEventListeners() {
        // Close button click
        this.closeBtn.addEventListener('click', () => this.close());
        
        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.close();
        });
    }
    
    /**
     * Launch sequence for Nira viewer
     * 1. Hide UI elements immediately
     * 2. Fly to camera position
     * 3. Open viewer after animation completes
     */
    launch() {
        // Hide UI elements first
        this.hideUIElements();
        
        // Get map manager
        const mapManager = getMapManager();
        if (!mapManager || !mapManager.getMap()) {
            console.error('[NiraManager] Map not initialized, opening Nira directly');
            this.openIframe();
            return;
        }
        
        // Get map instance
        const map = mapManager.getMap();
        
        // Stop spinning if active
        if (mapManager.spinControl) {
            mapManager.spinControl.stopSpinning();
            mapManager.spinControl.setFlying(true);
        }
        
        // Execute the flyTo animation
        map.flyTo({
            center: this.cameraPosition.center,
            zoom: this.cameraPosition.zoom,
            pitch: this.cameraPosition.pitch,
            bearing: this.cameraPosition.bearing,
            duration: 2000,
            essential: true
        });
        
        // Open iframe after animation completes
        map.once('moveend', () => {
            console.log('[NiraManager] Camera transition complete, opening Nira');
            
            // Reset flying state
            if (mapManager.spinControl) {
                mapManager.spinControl.setFlying(false);
            }
            
            // Open the iframe
            this.openIframe();
        });
    }

    /**
     * Launch Nira with a specific camera position
     * This is the main entry point that should be used by all launch triggers
     * @param {Object} position Camera position with center, zoom, pitch, bearing
     */
    launchWithPosition(position) {
        // Set the camera position first
        this.setCameraPosition(position);
        
        // Then start the launch sequence
        this.launch();
    }
    
    /**
     * Helper function to forcefully set display style
     * @param {HTMLElement} element The element to modify
     * @param {string} displayValue The display value to set
     * @param {string} description Description for logging
     */
    forceDisplayStyle(element, displayValue, description) {
        if (!element) return;
        
        console.log(`[NiraManager] Forcing ${description} display to ${displayValue}`);
        
        // Set inline style
        element.style.display = displayValue;
        
        // Also set !important style using setAttribute
        element.setAttribute('style', `display: ${displayValue} !important`);
        
        // Add a class for additional control
        if (displayValue === 'none') {
            element.classList.add('nira-hidden');
            element.classList.remove('nira-visible');
        } else {
            element.classList.add('nira-visible');
            element.classList.remove('nira-hidden');
        }
    }
    
    /**
     * Hide UI elements when launching Nira
     */
    hideUIElements() {
        console.log('[NiraManager] Hiding UI elements');
        
        // Always get fresh references to UI elements in case they've been dynamically created/updated
        this.cityNav = document.getElementById('cityNav');
        // this.infoPanel = document.querySelector('.info-container');
        this.dataPanel = document.querySelector('.data-panel');
        this.tools = document.getElementById('tools');
        this.niraButtons = document.querySelectorAll('.launch-nira-btn');
        this.niraTools = document.getElementById('niraTools');
        
        // Get all UI elements to hide
        const uiElements = [
            { el: this.cityNav, name: 'city navigation' },
            // { el: this.infoPanel, name: 'info panel' },
            { el: this.dataPanel, name: 'data panel' },
            { el: this.tools, name: 'tools' }
        ];
        
        // Hide all Nira launch buttons
        this.niraButtons.forEach(button => {
            this.forceDisplayStyle(button, 'none', 'nira launch button');
        });
        
        // Show Nira tools navigation
        if (this.niraTools) {
            this.forceDisplayStyle(this.niraTools, 'flex', 'nira tools navigation');
        }

        // Hide all UI elements at once
        uiElements.forEach(({ el, name }) => {
            if (el) {
                console.log(`[NiraManager] Found ${name}:`, el);
                // First remove visible class
                el.classList.remove('visible');
                // Force remove again after a frame to ensure it's gone
                requestAnimationFrame(() => {
                    el.classList.remove('visible');
                    // Then force display none
                    this.forceDisplayStyle(el, 'none', name);
                    // Remove any other visibility classes
                    el.classList.remove('nira-visible');
                });
            } else {
                console.warn(`[NiraManager] ${name} element not found`);
            }
        });
    }
    
    /**
     * Show UI elements when closing Nira
     */
    showUIElements() {
        console.log('[NiraManager] Showing UI elements');
        
        // Always get fresh references to UI elements in case they've been dynamically created/updated
        this.cityNav = document.getElementById('cityNav');
        // this.infoPanel = document.querySelector('.info-container');
        this.dataPanel = document.querySelector('.data-panel');
        this.tools = document.getElementById('tools');
        this.niraButtons = document.querySelectorAll('.launch-nira-btn');
        this.niraTools = document.getElementById('niraTools');
        
        // Get all UI elements with their display styles
        const uiElements = [
            { el: this.cityNav, name: 'city navigation', display: 'flex' },
            // { el: this.infoPanel, name: 'info panel', display: 'block' },
            { el: this.dataPanel, name: 'data panel', display: 'block' },
            { el: this.tools, name: 'tools', display: 'block' }
        ];
        
        // Show all Nira launch buttons
        this.niraButtons.forEach(button => {
            this.forceDisplayStyle(button, 'block', 'nira launch button');
        });
        
        // Hide Nira tools navigation
        if (this.niraTools) {
            this.forceDisplayStyle(this.niraTools, 'none', 'nira tools navigation');
        }

        // Show all UI elements at once
        uiElements.forEach(({ el, name, display }) => {
            if (el) {
                console.log(`[NiraManager] Found ${name}:`, el);
                this.forceDisplayStyle(el, display, name);
                
                // Add visible class back
                el.classList.add('visible');
            } else {
                console.warn(`[NiraManager] ${name} element not found`);
            }
        });
    }
    
    openIframe() {
        // Set iframe src
        this.iframe.src = this.niraUrl;
        
        // Show container with animation
        requestAnimationFrame(() => {
            this.container.classList.add('visible');
        });
        
        // Initialize Nira API when iframe loads
        this.iframe.onload = () => {
            console.log('[NiraManager] Iframe loaded, initializing Nira API');
            this.initializeNiraAPI();
        };
    }
    
    /**
     * Initialize the Nira API with the iframe
     * This creates a global niraViewer object that can be used to control the viewer
     */
    initializeNiraAPI() {
        try {
            // Check if NiraAPI is available
            if (typeof NiraAPI === 'undefined') {
                console.error('[NiraManager] NiraAPI not found. Make sure the Nira SDK script is loaded.');
                // Fall back to our other methods if NiraAPI is not available
                this.setupAutoPlayFallback();
                return;
            }
            
            // Initialize the Nira API viewer with configuration options
            window.niraViewer = new NiraAPI.Viewer(this.iframe, {
                // Hide Nira's built-in navigation tools since we're providing our own
                showNavigationTools: false,
                // Hide Nira's measurement tool
                showMeasureTool: false
            });
            
            this.niraViewer = window.niraViewer;
            
            console.log('[NiraManager] Nira API initialized successfully');
            
            // Set up event listeners for asset loading/unloading
            if (this.niraViewer.on) {
                // Show niraTools when asset finishes loading
                this.niraViewer.on("asset_load_finish", () => {
                    console.log('[NiraManager] Asset loaded, showing Nira tools');
                    if (this.niraTools) {
                        this.niraTools.style.display = "flex";
                    }
                });
                
                // Hide niraTools when asset is unloaded
                this.niraViewer.on("asset_unload", () => {
                    console.log('[NiraManager] Asset unloaded, hiding Nira tools');
                    if (this.niraTools) {
                        this.niraTools.style.display = "none";
                    }
                });
                
                // Handle toolmode changes
                this.niraViewer.on("toolmode", (toolmode) => {
                    console.log(`[NiraManager] Toolmode changed to: ${toolmode}`);
                    const activeButtonId = `toolbutton_${toolmode}`;
                    
                    // Update active class on tool buttons
                    document.querySelectorAll(".toolbutton").forEach(buttonEle => {
                        if (buttonEle.id == activeButtonId) {
                            buttonEle.classList.add("active");
                        } else {
                            buttonEle.classList.remove("active");
                        }
                    });
                });
            }
            
            // Set up additional event listeners for the viewer
            this.setupNiraEventListeners();
            
            console.log('[NiraManager] Nira event listeners set up successfully');
        } catch (error) {
            console.error('[NiraManager] Error initializing Nira API:', error);
            // Fall back to our other methods if initialization fails
            this.setupAutoPlayFallback();
        }
    }
    
    /**
     * Set up event listeners for the Nira viewer
     */
    setupNiraEventListeners() {
        if (!this.niraViewer) return;
        
        try {
            console.log('[NiraManager] Setting up Nira event listeners');
            
            // Check if addEventListener method exists
            if (typeof this.niraViewer.addEventListener === 'function') {
                // Listen for viewer ready event
                this.niraViewer.addEventListener('ready', () => {
                    console.log('[NiraManager] Nira viewer is ready');
                    this.autoStartViewer();
                });
                
                // Listen for load progress
                this.niraViewer.addEventListener('loadProgress', (progress) => {
                    console.log(`[NiraManager] Load progress: ${progress * 100}%`);
                });
                
                // Listen for load complete
                this.niraViewer.addEventListener('loadComplete', () => {
                    console.log('[NiraManager] Load complete');
                });
            } else {
                // Alternative approach if addEventListener is not available
                console.log('[NiraManager] addEventListener not available, using alternative approach');
                
                // Just call autoStartViewer directly
                this.autoStartViewer();
                
                // Check if the viewer has an on method (common alternative to addEventListener)
                if (typeof this.niraViewer.on === 'function') {
                    this.niraViewer.on('ready', () => {
                        console.log('[NiraManager] Nira viewer is ready (on method)');
                        this.autoStartViewer();
                    });
                }
            }
        } catch (error) {
            console.error('[NiraManager] Error setting up Nira event listeners:', error);
            // Still try to auto-start even if event listeners fail
            this.autoStartViewer();
        }
    }
    
    /**
     * Auto-start the viewer (click the play button)
     */
    autoStartViewer() {
        if (!this.niraViewer) return;
        
        try {
            console.log('[NiraManager] Attempting to auto-start the viewer');
            
            // Try to start the viewer using the API
            if (typeof this.niraViewer.play === 'function') {
                this.niraViewer.play();
                console.log('[NiraManager] Called play() method');
            }
            
            // Try to start the viewer using the start method
            if (typeof this.niraViewer.start === 'function') {
                this.niraViewer.start();
                console.log('[NiraManager] Called start() method');
            }
            
            // Try to set the tool mode to orbit (default)
            if (typeof this.niraViewer.setToolMode === 'function' && typeof NiraAPI !== 'undefined') {
                this.niraViewer.setToolMode(NiraAPI.Toolmode.Orbit);
                console.log('[NiraManager] Set tool mode to Orbit');
            }
        } catch (error) {
            console.error('[NiraManager] Error auto-starting viewer:', error);
        }
    }
    
    /**
     * Fallback method for auto-play if Nira API initialization fails
     * Uses multiple strategies to maximize chances of success despite cross-origin restrictions
     */
    setupAutoPlayFallback() {
        console.log('[NiraManager] Setting up auto-play fallback');
        
        // Try direct access when iframe loads
        this.tryClickPlayButton();
        
        // Also try after a short delay to allow for dynamic content to load
        setTimeout(() => this.tryClickPlayButton(), 1000);
        setTimeout(() => this.tryClickPlayButton(), 2000);
        setTimeout(() => this.tryClickPlayButton(), 3000);
        
        // Try postMessage strategy
        this.tryPostMessageAutoPlay();
        
        // Use polling as a fallback
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = setInterval(() => {
            attempts++;
            this.tryClickPlayButton();
            
            // Also try postMessage every few attempts
            if (attempts % 3 === 0) {
                this.tryPostMessageAutoPlay();
            }
            
            if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
                console.log('[NiraManager] Reached maximum auto-play attempts');
            }
        }, 1000);
        
        // Clear interval when iframe is closed
        this.closeBtn.addEventListener('click', () => {
            clearInterval(pollInterval);
        });
    }
    
    /**
     * Attempts to use postMessage to communicate with the iframe
     * This is a more reliable way to communicate with cross-origin iframes if they support it
     */
    tryPostMessageAutoPlay() {
        try {
            console.log('[NiraManager] Attempting postMessage auto-play');
            
            // Try different message formats that the iframe might be listening for
            const messages = [
                { action: 'play' },
                { command: 'play' },
                { type: 'play' },
                'play',
                { action: 'start' },
                { command: 'start' },
                { type: 'start' },
                'start',
                { action: 'autoplay', value: true },
                { autoplay: true }
            ];
            
            // Send each message format
            messages.forEach(message => {
                this.iframe.contentWindow.postMessage(message, '*');
            });
            
            return true;
        } catch (error) {
            console.log('[NiraManager] Error sending postMessage:', error);
            return false;
        }
    }
    
    /**
     * Attempts to click the play button in the iframe
     * Handles cross-origin restrictions gracefully
     */
    tryClickPlayButton() {
        try {
            // Try to access iframe content
            const iframeWindow = this.iframe.contentWindow;
            const iframeDocument = iframeWindow.document;
            
            // Look for play button using various selectors that might match
            const possibleSelectors = [
                '.play-button',
                'button.play',
                '[aria-label="Play"]',
                'button[title*="Play"]',
                'button:has(svg)',
                '.start-button',
                '.viewer-start',
                'button.primary',
                'button.start'
            ];
            
            // Try each selector
            for (const selector of possibleSelectors) {
                const playButton = iframeDocument.querySelector(selector);
                if (playButton) {
                    console.log('[NiraManager] Found play button with selector:', selector);
                    playButton.click();
                    return true;
                }
            }
            
            // If no specific selector worked, try a more general approach
            // Look for any button elements
            const buttons = iframeDocument.querySelectorAll('button');
            for (const button of buttons) {
                // Check if button text contains "play" or "start"
                const buttonText = button.textContent.toLowerCase();
                if (buttonText.includes('play') || buttonText.includes('start')) {
                    console.log('[NiraManager] Found button with play/start text');
                    button.click();
                    return true;
                }
            }
            
            console.log('[NiraManager] No play button found yet');
            return false;
        } catch (error) {
            // This is expected due to cross-origin restrictions
            console.log('[NiraManager] Could not access iframe content due to cross-origin policy');
            return false;
        }
    }
    
    close() {
        // Hide container
        this.container.classList.remove('visible');
        
        // Clear iframe src after transition
        setTimeout(() => {
            this.iframe.src = '';
        }, 300);
        
        // Show UI elements
        this.showUIElements();
    }
    
    /**
     * Set a custom camera position to fly to before opening Nira
     * @param {Object} position Camera position with center, zoom, pitch, bearing
     */
    setCameraPosition(position) {
        if (position && position.center) {
            this.cameraPosition = {
                center: position.center,
                zoom: position.zoom || this.cameraPosition.zoom,
                pitch: position.pitch || this.cameraPosition.pitch,
                bearing: position.bearing || this.cameraPosition.bearing
            };
            console.log('[NiraManager] Updated camera position:', this.cameraPosition);
        }
    }
}

// Create and export singleton instance
let niraManagerInstance = null;

export const createNiraManager = () => {
    if (!niraManagerInstance) {
        niraManagerInstance = new NiraManager();
    }
    return niraManagerInstance;
};

export const getNiraManager = () => {
    return niraManagerInstance;
};